import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { EFFECT_CATALOG, type EffectCatalogEntry } from "@/lib/pharmacology/annotationInterpreter";
import { toast } from "@/lib/safeToast";

export type EffectProfileRow = {
  id?: string;
  effect_key: string;
  benefit_0_100: number | null;
  adverse_0_100: number | null;
  confidence_0_100: number | null;
  evidence_level: string | null;
  note: string | null;
};

const EVIDENCE_LEVELS = ["forte", "moderada", "fraca", "expert", "empírica", "rascunho"];

export function PDPotentialSliders({ substanceId }: { substanceId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [rows, setRows] = useState<Record<string, EffectProfileRow>>({});
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: existing = [], isFetching, error } = useQuery({
    queryKey: ["sep", substanceId],
    enabled: !!substanceId,
    queryFn: async () => {
      const { data, error: readError } = await (supabase as any)
        .from("substance_effect_profiles")
        .select("*")
        .eq("substance_id", substanceId)
        .order("effect_key");
      if (readError) throw readError;
      return data ?? [];
    },
  });

  useEffect(() => {
    const next: Record<string, EffectProfileRow> = {};
    for (const effect of EFFECT_CATALOG) {
      next[effect.key] = {
        effect_key: effect.key,
        benefit_0_100: null,
        adverse_0_100: null,
        confidence_0_100: null,
        evidence_level: null,
        note: null,
      };
    }
    for (const row of existing as any[]) {
      const current = next[row.effect_key];
      if (!current?.id || new Date(row.updated_at ?? row.created_at ?? 0).getTime() >= new Date((current as any).updated_at ?? (current as any).created_at ?? 0).getTime()) {
        next[row.effect_key] = { ...current, ...row };
      }
    }
    setRows(next);
  }, [existing, substanceId]);

  const filtered = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return EFFECT_CATALOG.filter((effect) => !query || effect.label.toLowerCase().includes(query) || effect.axis.includes(query) || effect.key.includes(query));
  }, [filter]);

  function update(key: string, patch: Partial<EffectProfileRow>) {
    setRows((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  }

  async function persistRow(effect: EffectCatalogEntry, notify = true) {
    if (!user?.id) throw new Error("Sessão expirada.");
    const row = rows[effect.key];
    if (!row) return null;
    const payload = {
      owner_id: user.id,
      substance_id: substanceId,
      formulation_id: null,
      effect_key: row.effect_key,
      benefit_0_100: row.benefit_0_100,
      adverse_0_100: row.adverse_0_100,
      confidence_0_100: row.confidence_0_100,
      evidence_level: row.evidence_level,
      note: row.note,
    };

    let result: any;
    if (row.id) {
      result = await (supabase as any)
        .from("substance_effect_profiles")
        .update(payload)
        .eq("id", row.id)
        .eq("owner_id", user.id)
        .select("*")
        .single();
    } else {
      const { data: matches = [], error: readError } = await (supabase as any)
        .from("substance_effect_profiles")
        .select("*")
        .eq("owner_id", user.id)
        .eq("substance_id", substanceId)
        .eq("effect_key", row.effect_key)
        .is("formulation_id", null)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (readError) throw readError;
      result = matches?.[0]?.id
        ? await (supabase as any).from("substance_effect_profiles").update(payload).eq("id", matches[0].id).eq("owner_id", user.id).select("*").single()
        : await (supabase as any).from("substance_effect_profiles").insert(payload).select("*").single();
    }
    if (result.error) throw result.error;
    setRows((current) => ({ ...current, [effect.key]: { ...current[effect.key], ...result.data } }));
    if (notify) toast.success(`${effect.label} salvo.`);
    return result.data;
  }

  async function saveOne(effect: EffectCatalogEntry) {
    try {
      await persistRow(effect, true);
    } catch (saveError: any) {
      console.error("[PDPotentialSliders] save failed", { effect: effect.key, saveError });
      toast.error(saveError?.message ?? `Falha ao salvar ${effect.label}.`);
    }
  }

  async function saveAll() {
    const dirty = Object.values(rows).filter((row) => row.benefit_0_100 != null || row.adverse_0_100 != null || row.confidence_0_100 != null || row.note || row.evidence_level);
    if (!dirty.length) return toast.message("Nenhum efeito preenchido.");
    setSaving(true);
    try {
      for (const row of dirty) {
        const effect = EFFECT_CATALOG.find((item) => item.key === row.effect_key);
        if (effect) await persistRow(effect, false);
      }
      await qc.invalidateQueries({ queryKey: ["sep", substanceId] });
      toast.success(`${dirty.length} perfis PD salvos sem duplicação.`);
    } catch (saveError: any) {
      console.error("[PDPotentialSliders] save all failed", saveError);
      toast.error(saveError?.message ?? "Falha ao salvar perfis PD.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="min-w-0 space-y-4 overflow-hidden p-3 sm:p-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="font-medium">PD / Potenciais 0–100</div>
          <Badge variant="outline">{EFFECT_CATALOG.length} efeitos</Badge>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-2 sm:ml-auto sm:grid-cols-[minmax(12rem,20rem)_auto]">
          <Input
            aria-label="Filtrar efeitos farmacodinâmicos"
            placeholder="Filtrar efeito, eixo…"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className="min-w-0"
          />
          <Button onClick={saveAll} disabled={isFetching || saving} className="w-full sm:w-auto">
            {saving ? "Salvando…" : "Salvar tudo"}
          </Button>
        </div>
      </div>
      <div className="text-xs leading-relaxed text-muted-foreground">
        Cada dimensão pode representar benefício e/ou risco. O valor descreve potencial farmacodinâmico relativo; confiança e evidência ficam separadas.
      </div>
      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          Falha ao carregar perfis PD: {(error as any)?.message}
        </div>
      ) : null}

      <div className="grid min-w-0 grid-cols-1 gap-3 xl:grid-cols-2">
        {filtered.map((effect) => {
          const row = rows[effect.key];
          if (!row) return null;
          const showBenefit = effect.polarity !== "adverse";
          const showAdverse = effect.polarity !== "benefit";
          return (
            <div key={effect.key} className="min-w-0 space-y-3 overflow-hidden rounded-xl border bg-card p-3 sm:p-4">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-medium">{effect.label}</div>
                  <div className="break-words text-[10px] uppercase tracking-wide text-muted-foreground">{effect.axis} · {effect.polarity}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => saveOne(effect)} className="shrink-0">Salvar</Button>
              </div>
              {showBenefit ? <SliderRow label="Benefício" tone="text-success" value={row.benefit_0_100} onChange={(value) => update(effect.key, { benefit_0_100: value })} hint={effect.benefit_prompt} /> : null}
              {showAdverse ? <SliderRow label="Risco/adverso" tone="text-destructive" value={row.adverse_0_100} onChange={(value) => update(effect.key, { adverse_0_100: value })} hint={effect.adverse_prompt} /> : null}
              <SliderRow label="Confiança" tone="text-muted-foreground" value={row.confidence_0_100} onChange={(value) => update(effect.key, { confidence_0_100: value })} />
              <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(8.75rem,0.45fr)_minmax(0,1fr)]">
                <select
                  aria-label={`Nível de evidência para ${effect.label}`}
                  className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base sm:h-9 sm:text-xs"
                  value={row.evidence_level ?? ""}
                  onChange={(event) => update(effect.key, { evidence_level: event.target.value || null })}
                >
                  <option value="">Evidência…</option>
                  {EVIDENCE_LEVELS.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
                <Input
                  aria-label={`Nota para ${effect.label}`}
                  placeholder="Nota curta"
                  value={row.note ?? ""}
                  onChange={(event) => update(effect.key, { note: event.target.value })}
                  className="h-10 min-w-0 text-base sm:h-9 sm:text-xs"
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SliderRow({ label, tone, value, onChange, hint }: { label: string; tone?: string; value: number | null; onChange: (value: number | null) => void; hint?: string }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className={tone ?? "text-muted-foreground"}>{label}</span>
        <span className="shrink-0 font-mono tabular-nums">{value ?? "—"}</span>
      </div>
      <Slider aria-label={label} className="w-full min-w-0" value={[value ?? 0]} min={0} max={100} step={1} onValueChange={(next) => onChange(next[0])} />
      {hint ? <div className="break-words text-[10px] leading-relaxed text-muted-foreground">{hint}</div> : null}
    </div>
  );
}
